import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useSub } from "@/context/SubscriptionContext";
import { Button } from "@/components/ui/button";
import { Check, Lightning, Crown, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { isNativeApp } from "@/lib/native";

const TIERS = [
  {
    id: "free", name: "Free", icon: Sparkle, monthly: 0, yearly: 0,
    tagline: "Explore the market",
    features: ["Live market dashboard", "Full stock browser (92 tickers)", "Watchlist", "AI stock scores & factors", "5 AI assistant messages / day"],
  },
  {
    id: "pro", name: "Pro", icon: Lightning, monthly: 29, yearly: 290, popular: true,
    lookup: { monthly: "pro_monthly", yearly: "pro_yearly" },
    tagline: "Find and time opportunities",
    features: ["Everything in Free", "AI Market Scanner", "Probabilistic forecasts", "Forecast scorecard", "Price & AI-score alerts", "50 AI messages / day"],
  },
  {
    id: "elite", name: "Elite", icon: Crown, monthly: 99, yearly: 990,
    lookup: { monthly: "elite_monthly", yearly: "elite_yearly" },
    tagline: "Build and validate strategies",
    features: ["Everything in Pro", "Strategy backtesting", "Custom strategy builder", "Portfolio (basket) backtests", "Strategy alerts on watchlist", "Unlimited AI assistant"],
  },
];

export default function Pricing() {
  const nav = useNavigate();
  const { effectiveTier, status } = useSub();
  const [period, setPeriod] = useState("monthly");
  const [busy, setBusy] = useState("");

  if (isNativeApp()) {
    return (
      <div className="text-center py-16" data-testid="pricing-native">
        <h1 className="font-heading text-2xl font-bold">Your plan</h1>
        <p className="mt-3 text-sm text-muted-foreground">Current plan: <span className="text-white capitalize">{effectiveTier}</span></p>
      </div>
    );
  }

  const subscribe = async (tier) => {
    setBusy(tier.id);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: tier.lookup[period], origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setBusy(""); }
  };

  return (
    <div>
      <div className="text-center">
        <div className="label-xs">Plans & Pricing</div>
        <h1 className="mt-1 font-heading text-4xl font-bold tracking-tighter">Choose your edge</h1>
        <p className="mt-2 text-sm text-muted-foreground">Start with a 7-day free trial on any paid plan. Cancel anytime.</p>

        <div className="mt-5 inline-flex border border-border rounded-sm overflow-hidden" data-testid="billing-toggle">
          <button data-testid="toggle-monthly" onClick={() => setPeriod("monthly")} className={`px-4 py-2 text-sm transition-colors duration-200 ${period === "monthly" ? "bg-volt text-white" : "text-muted-foreground hover:text-white"}`}>Monthly</button>
          <button data-testid="toggle-yearly" onClick={() => setPeriod("yearly")} className={`px-4 py-2 text-sm transition-colors duration-200 ${period === "yearly" ? "bg-volt text-white" : "text-muted-foreground hover:text-white"}`}>Annual <span className="text-signal-green">·2mo free</span></button>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl mx-auto">
        {TIERS.map((t) => {
          const current = effectiveTier === t.id;
          const price = period === "yearly" ? t.yearly : t.monthly;
          return (
            <div key={t.id} data-testid={`plan-${t.id}`}
              className={`relative border bg-card p-6 rounded-sm flex flex-col ${t.popular ? "border-volt shadow-[0_0_40px_-15px_rgba(0,122,255,0.6)]" : "border-border"}`}>
              {t.popular && <span className="absolute -top-2.5 left-6 bg-volt text-white text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm">Most Popular</span>}
              <div className="flex items-center gap-2">
                <t.icon size={20} className={t.id === "elite" ? "text-signal-yellow" : "text-volt"} weight="fill" />
                <span className="font-heading text-lg font-semibold">{t.name}</span>
              </div>
              <div className="mt-4 flex items-end gap-1">
                <span className="mono text-4xl font-bold">${price}</span>
                <span className="text-muted-foreground text-sm mb-1">/{period === "yearly" ? "yr" : "mo"}</span>
              </div>
              {period === "yearly" && t.monthly > 0 && (
                <div className="mt-1.5 inline-flex items-center gap-1.5 self-start border border-signal-green/40 bg-signal-green/10 text-signal-green mono text-[11px] px-2 py-0.5 rounded-sm" data-testid={`savings-${t.id}`}>
                  Save ${t.monthly * 12 - t.yearly}/yr · {Math.round((1 - t.yearly / (t.monthly * 12)) * 100)}% off
                </div>
              )}
              {period === "yearly" && t.monthly > 0 && (
                <div className="text-[11px] text-muted-foreground mt-1">${(t.yearly / 12).toFixed(2)}/mo billed annually</div>
              )}
              <div className="text-xs text-muted-foreground mt-1">{t.tagline}</div>

              <ul className="mt-5 space-y-2 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm"><Check size={15} className="mt-0.5 shrink-0 text-signal-green" /><span>{f}</span></li>
                ))}
              </ul>

              {t.id === "free" ? (
                <Button disabled variant="outline" className="mt-6 rounded-sm border-border">{current ? "Current plan" : "Free forever"}</Button>
              ) : current ? (
                <Button data-testid={`manage-${t.id}`} onClick={() => nav("/account")} variant="outline" className="mt-6 rounded-sm border-volt/40 text-volt">Manage plan</Button>
              ) : (
                <Button data-testid={`subscribe-${t.id}`} disabled={busy === t.id} onClick={() => subscribe(t)}
                  className={`mt-6 rounded-sm ${t.id === "elite" ? "bg-signal-yellow hover:bg-signal-yellow/90 text-black" : "bg-volt hover:bg-volt/90 text-white"}`}>
                  {busy === t.id ? "Redirecting…" : "Start 7-day trial"}
                </Button>
              )}
            </div>
          );
        })}
      </div>
      <p className="mt-6 text-center mono text-[11px] text-muted-foreground">Test mode · use Stripe test card 4242 4242 4242 4242, any future date & CVC. Paper trading only — not financial advice.</p>
    </div>
  );
}
